import { useState, useEffect, useRef, MutableRefObject } from 'react';
import MessageActions from '../../message-actions/MessageActions';
import { convertMarkdownToHTML } from '../../../utils/convertMarkdownToHTML';

const TypingMessage = ({
	message,
	chatId,
	typingSpeed = 3000,
	isAuth,
	audioRef
}: {
	message: string,
	chatId?: string,
	typingSpeed: number,
	isAuth?: boolean,
	audioRef?: MutableRefObject<HTMLAudioElement | { pause: () => void; paused: boolean; currentTime: number } | null>
}) => {
	const [arrayHtml, setArrayHtml] = useState<string[][]>();
	const [showActions, setShowActions] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	function isMobile() {
		return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
		  navigator.userAgent
		);
	  }

	function splitTextPreservingHtml(text: string, maxLineLength = isAuth ? (isMobile() ? 42 : 52) : 80): string[] {
		const cleanedText = text
		  .replace(/<br\s*\/?>/gi, '\n')
		  .replace(/\n{3,}/g, '\n\n')
		  .trim();
	  
		const lines: string[] = [];
		let currentLine = '';
		let inTag = false;
		let tagBuffer = '';
		let visible = 0;                 // counts only visible chars (outside tags)
		let anchorDepth = 0;             // >0 means inside <a>…</a>
		let lastSafeHtmlLen: number|null = null;  // string length of currentLine at last safe split
		let lastSafeVisible: number|null = null;  // visible count at last safe split
	  
		const pushLine = (sliceLen?: number, sliceVisible?: number) => {
		  if (sliceLen != null) {
			// push prefix
			const head = currentLine.slice(0, sliceLen);
			if (head.trim()) lines.push(head.trim());
			// keep remainder in buffer
			currentLine = currentLine.slice(sliceLen);
			visible = Math.max(0, visible - (sliceVisible ?? 0));
		  } else {
			const s = currentLine.trim();
			if (s) lines.push(s);
			currentLine = '';
			visible = 0;
		  }
		  lastSafeHtmlLen = null;
		  lastSafeVisible = null;
		};
	  
		for (let i = 0; i < cleanedText.length; i++) {
		  const ch = cleanedText[i];
	  
		  // tag start
		  if (ch === '<') {
			inTag = true;
			tagBuffer = '<';
			continue;
		  }
	  
		  // tag end
		  if (ch === '>' && inTag) {
			tagBuffer += '>';
			inTag = false;
	  
			const lower = tagBuffer.toLowerCase();
	  
			// <li> or <p> starts a new array element
			if (/^<(li|p)[\s>]/.test(lower)) {
				if (currentLine.trim()) pushLine();   // flush whatever was before
				currentLine += tagBuffer;             // start chunk with the opening tag
				tagBuffer = '';
				continue;
			}
	  
			// </li> closes this element; include it and flush
			if (lower.startsWith('</li')) {
			  currentLine += tagBuffer;
			  pushLine();                           // flush li chunk
			  tagBuffer = '';
			  continue;
			}
	  
			// track anchors so we don't split inside links
			if (/^<a[\s>]/.test(lower)) {
			  anchorDepth++;
			} else if (lower.startsWith('</a')) {
			  anchorDepth = Math.max(0, anchorDepth - 1);
			}
	  
			currentLine += tagBuffer;               // append any other tag
			tagBuffer = '';
			continue;
		  }
	  
		  // inside tag
		  if (inTag) {
			tagBuffer += ch;
			continue;
		  }
	  
		  // newline boundary
		  if (ch === '\n') {
			if (currentLine.trim()) pushLine();
			continue;
		  }
	  
		  // visible text
		  currentLine += ch;
		  visible += 1;
	  
		  // update "last safe split" marker on spaces OUTSIDE anchors
		  if (ch === ' ' && anchorDepth === 0) {
			lastSafeHtmlLen = currentLine.length;   // HTML length up to and including this space
			lastSafeVisible = visible;              // visible count up to this space
		  }
	  
		  // if we've exceeded the limit, split at the LAST safe space BEFORE the limit
		  if (visible > maxLineLength && lastSafeHtmlLen != null) {
			// push prefix up to last safe space
			pushLine(lastSafeHtmlLen, lastSafeVisible ?? 0);
			// NOTE: we do NOT add a delay here; the loop continues,
			// so remainder stays in currentLine and visible recalculated correctly
		  }
		}
	  
		// flush tail
		if (currentLine.trim()) pushLine();
	  
		return lines;
	}   

	useEffect(() => {
		const chunks = convertMarkdownToHTML(message);
		let fullOutputArray: string[][] = [];
		let charIndex = 0;

		setShowActions(false);

		const typeNext = () => {
			const lines = chunks
				.split(/\n+/)
				.filter(chunk => chunk.trim() !== '')
				.map(chunk => splitTextPreservingHtml(chunk));

			if (charIndex < lines.length) {
				fullOutputArray = [...fullOutputArray, lines[charIndex]];
				setArrayHtml(fullOutputArray);
				charIndex++;
				setTimeout(typeNext, typingSpeed);
			} else {
				setShowActions(true);
			}
		};

		typeNext();
	}, [message]);

	return (
		<div className="bot-message dark:text-white font-normal text-sm" ref={containerRef}>
			{arrayHtml?.map((arr) =>
				arr.map((item, index) => (
					<p
						style={{ animationDelay: `${index * 200}ms` }}
						key={item + index}
						className="fade-word"
						dangerouslySetInnerHTML={{ __html: item }}
					/>
				))
			)}
			{chatId && showActions && (
				<MessageActions chatId={chatId} message={message} topicId={chatId} audioRef={audioRef} showOptions />
			)}
		</div>
	);
};

export default TypingMessage;
